import { action, makeAutoObservable } from "mobx";
import { EffectDescription, FilterDescription, TitleDescription, TransitionDescription } from "../config/config";
import { CompositionService } from "../services/CompositionService";
import { CompositionStateStore } from "./CompositionStateStore";
import type { ScriptChunkV1 } from "../types/ScriptChunk";

export type ThemeVariants = "dark" | "light" | "system";

/**
 * Reference to a visual element on the canvas.
 * For regular clips: just clipId
 * For ScriptLayerClip: clipId + sourceIndex to identify specific video source
 */
export interface VisualElementRef {
    clipId: string;
    sourceIndex?: number;  // undefined for regular clips, number for ScriptLayerClip video sources
}

/**
 * Crop configuration for a visual element
 */
export interface CropConfig {
    /** X offset of the crop region (0-1, relative to original width) */
    x: number;
    /** Y offset of the crop region (0-1, relative to original height) */
    y: number;
    /** Width of crop region (0-1, relative to original width) */
    width: number;
    /** Height of crop region (0-1, relative to original height) */
    height: number;
}

export type CropScope = 'current' | 'all';
export type SceneEditScope = 'scene' | 'all';

type DraggingObjectPayload =
    | {
    type: "media";
    payload: any;
}
    | {
    type: "text-preset";
    payload: TitleDescription;
};

class ApplicationStoreInstance {
    isLoading: boolean = true;
    isRendering: boolean = false;
    selectedClipId: string | null = null;
    selectedVisualElement: VisualElementRef | null = null;  // For canvas visual element selection
    selectedTransitionId: string | null = null;
    editingTextClipId: string | null = null;
    subtitlesClipId: string | null = null;
    transitions: Record<string, TransitionDescription> = {};
    filters: Record<string, FilterDescription> = {};
    effects: Record<string, EffectDescription> = {};
    draggingObject: DraggingObjectPayload | null = null;
    selectedRange: {start: number; end: number} | null = null;
    rangeToolEnabled: boolean = false;
    slipToolEnabled: boolean = false;

    selectedScriptLayerRange: {start: number; end: number} | null = null;
    selectedSceneIndex: number | null = null;  // Index of selected scene in script clip (0-based)
    theme: ThemeVariants = "system";

    // Crop mode state
    isCropMode: boolean = false;
    cropScope: CropScope = 'current';
    // Scene edit scope for per-scene overrides (visual properties, captions styles, etc.)
    sceneEditScope: SceneEditScope = 'scene';
    /** Working crop config while in crop mode (before "Done" is clicked) */
    workingCrop: CropConfig = { x: 0, y: 0, width: 1, height: 1 };
    /** Zoom level for display in crop mode (1 = 100%) */
    cropZoom: number = 1;
    filtersPath: string | null = null;
    effectsPath: string | null = null;
    transitionsPath: string | null = null;
    titlesPath: string | null = null;
    subtitleStylesPath: string | null = null;
    animationsPath: string | null = null;

    // Pending "Add layer" insertion context (e.g., from timeline context menu).
    // Used to anchor insertion to a specific time and apply constraints like "end at scene end".
    pendingAddLayerContext: { startAtSec: number; constrainToSceneEnd?: boolean; createdAtMs: number } | null = null;

    // Pending "Add layer from transcript selection" context.
    // The transcript selection is in script-clip local time (same domain as WordNode payloads).
    // We keep both boundaries so the media selection can:
    // 1) Split the script into a scene spanning the selection
    // 2) Insert a layer constrained to exactly that scene range
    pendingAddLayerFromSelectionContext:
        | { startSec: number; endSec: number; createdAtMs: number }
        | null = null;

    // Pending "Replace placeholder" context (e.g., from placeholder clip context menu).
    // Used to route the next media selection from the Project sidebar into a placeholder slot.
    pendingPlaceholderReplaceContext: { placeholderClipId: string; createdAtMs: number } | null = null;

    // Pending "Change layout" context (e.g., from Scene bar context menu).
    // Used to open the Layout picker in the Properties sidebar for a specific scene.
    pendingSceneLayoutPickerContext: { sceneIndex: number; createdAtMs: number } | null = null;

    // Pending "Insert media into script" context (e.g., from transcript empty-line overlay).
    // Used to route the next media selection from the Project sidebar into the script spine at a clip-local time.
    pendingInsertIntoScriptContext:
        | { atClipLocalSec: number; preferExistingEmptyScene?: boolean; createdAtMs: number }
        | null = null;

    // Pending "Add Scene" (split selection into its own scene) context.
    // Set by the transcript toolbar, consumed by the Layout picker when a layout is chosen.
    pendingAddSceneFromSelectionContext:
        | { startSec: number; endSec: number; createdAtMs: number }
        | null = null;

    // Internal transcript "script section" clipboard (copy/paste).
    // Stores a chunk of the ScriptLayerClip spine (sources + scene props) captured from a transcript selection.
    scriptChunkClipboard: { chunk: ScriptChunkV1; createdAtMs: number } | null = null;

    // Editor-wide settings (in-memory for now; will move to persisted editor settings).
    applySmartTransitionsToNewScenes: boolean = true;

    constructor() {
        makeAutoObservable(this);
    }

    setApplySmartTransitionsToNewScenes(value: boolean) {
        this.applySmartTransitionsToNewScenes = Boolean(value);
    }

    setIsLoading(value: boolean) {
        this.isLoading = value;
    }

    setIsRendering(value: boolean) {
        this.isRendering = value;
    }

    setSelectedClipId(id: string | null) {
        // Clear previous selection ranges when switching clips
        if (this.selectedClipId !== id) {
            this.selectedScriptLayerRange = null;
            this.selectedSceneIndex = null;
            this.selectedRange = null;
            this.editingTextClipId = null;
        }
        this.selectedClipId = id;
        this.setSelectedTransitionId(null);

        // Only set selectedVisualElement if the clip actually has visual elements
        // AudioClips don't have visual elements; VideoClip and ImageClip always do
        // ScriptLayerClip may or may not have video sources
        if (id) {
            const clip = CompositionService.getClipById(id);
            const clipType = (clip as any)?.type;

            let hasVisual = false;
            if (clipType === 'video' || clipType === 'image' || clipType === 'captions' || clipType === 'text' || clipType === 'placeholder') {
                hasVisual = true;
            } else if (clipType === 'script') {
                // ScriptLayerClip only has visual if it has video entries
                const videoEntries = (clip as any)?.getVideoEntries?.() ?? [];
                hasVisual = videoEntries.length > 0;
            }
            // audio clips have no visual elements

            this.selectedVisualElement = hasVisual
                ? clipType === 'script'
                    ? { clipId: id, sourceIndex: 0 }
                    : { clipId: id }
                : null;
        } else {
            this.selectedVisualElement = null;
        }
    }

    clearSelectedClipId() {
        this.selectedClipId = null;
        this.selectedVisualElement = null;
        this.selectedScriptLayerRange = null;
        this.selectedSceneIndex = null;
        this.selectedRange = null;
        this.editingTextClipId = null;
        this.exitCropMode();
    }

    setPendingAddLayerContext(ctx: { startAtSec: number; constrainToSceneEnd?: boolean } | null) {
        if (!ctx) {
            this.pendingAddLayerContext = null;
            return;
        }
        // Only one "pending sidebar action" should be active at a time.
        this.pendingAddLayerFromSelectionContext = null;
        this.pendingPlaceholderReplaceContext = null;
        this.pendingInsertIntoScriptContext = null;
        this.pendingAddSceneFromSelectionContext = null;
        const startAtSec = Number(ctx.startAtSec ?? 0);
        this.pendingAddLayerContext = {
            startAtSec: Number.isFinite(startAtSec) ? startAtSec : 0,
            constrainToSceneEnd: Boolean(ctx.constrainToSceneEnd),
            createdAtMs: Date.now(),
        };
    }

    consumePendingAddLayerContext(maxAgeMs: number = 60_000): { startAtSec: number; constrainToSceneEnd?: boolean } | null {
        const ctx = this.pendingAddLayerContext;
        this.pendingAddLayerContext = null;
        if (!ctx) return null;
        if (Date.now() - (ctx.createdAtMs ?? 0) > maxAgeMs) return null;
        return { startAtSec: ctx.startAtSec, constrainToSceneEnd: ctx.constrainToSceneEnd };
    }

    peekPendingAddLayerContext(maxAgeMs: number = 60_000): { startAtSec: number; constrainToSceneEnd?: boolean } | null {
        const ctx = this.pendingAddLayerContext;
        if (!ctx) return null;
        if (Date.now() - (ctx.createdAtMs ?? 0) > maxAgeMs) return null;
        return { startAtSec: ctx.startAtSec, constrainToSceneEnd: ctx.constrainToSceneEnd };
    }

    setPendingAddLayerFromSelectionContext(ctx: { startSec: number; endSec: number } | null) {
        if (!ctx) {
            this.pendingAddLayerFromSelectionContext = null;
            return;
        }
        // Only one "pending sidebar action" should be active at a time.
        this.pendingAddLayerContext = null;
        this.pendingPlaceholderReplaceContext = null;
        this.pendingInsertIntoScriptContext = null;
        this.pendingAddSceneFromSelectionContext = null;

        const startSec = Number(ctx.startSec);
        const endSec = Number(ctx.endSec);
        if (!Number.isFinite(startSec) || !Number.isFinite(endSec) || endSec <= startSec) {
            this.pendingAddLayerFromSelectionContext = null;
            return;
        }

        this.pendingAddLayerFromSelectionContext = {
            startSec,
            endSec,
            createdAtMs: Date.now(),
        };
    }

    consumePendingAddLayerFromSelectionContext(
        maxAgeMs: number = 60_000,
    ): { startSec: number; endSec: number } | null {
        const ctx = this.pendingAddLayerFromSelectionContext;
        this.pendingAddLayerFromSelectionContext = null;
        if (!ctx) return null;
        if (Date.now() - (ctx.createdAtMs ?? 0) > maxAgeMs) return null;
        return { startSec: ctx.startSec, endSec: ctx.endSec };
    }

    peekPendingAddLayerFromSelectionContext(
        maxAgeMs: number = 60_000,
    ): { startSec: number; endSec: number } | null {
        const ctx = this.pendingAddLayerFromSelectionContext;
        if (!ctx) return null;
        if (Date.now() - (ctx.createdAtMs ?? 0) > maxAgeMs) return null;
        return { startSec: ctx.startSec, endSec: ctx.endSec };
    }

    setPendingPlaceholderReplaceContext(ctx: { placeholderClipId: string } | null) {
        if (!ctx) {
            this.pendingPlaceholderReplaceContext = null;
            return;
        }
        // Only one "pending sidebar action" should be active at a time.
        this.pendingAddLayerContext = null;
        this.pendingAddLayerFromSelectionContext = null;
        this.pendingSceneLayoutPickerContext = null;
        this.pendingInsertIntoScriptContext = null;
        this.pendingAddSceneFromSelectionContext = null;
        const placeholderClipId = String(ctx.placeholderClipId ?? "");
        if (!placeholderClipId) {
            this.pendingPlaceholderReplaceContext = null;
            return;
        }
        this.pendingPlaceholderReplaceContext = {
            placeholderClipId,
            createdAtMs: Date.now(),
        };
    }

    setPendingInsertIntoScriptContext(ctx: { atClipLocalSec: number; preferExistingEmptyScene?: boolean } | null) {
        if (!ctx) {
            this.pendingInsertIntoScriptContext = null;
            return;
        }
        // Only one "pending sidebar action" should be active at a time.
        this.pendingAddLayerContext = null;
        this.pendingAddLayerFromSelectionContext = null;
        this.pendingPlaceholderReplaceContext = null;
        this.pendingSceneLayoutPickerContext = null;
        this.pendingAddSceneFromSelectionContext = null;

        const at = Number(ctx.atClipLocalSec ?? 0);
        if (!Number.isFinite(at) || at < 0) {
            this.pendingInsertIntoScriptContext = null;
            return;
        }
        this.pendingInsertIntoScriptContext = {
            atClipLocalSec: at,
            preferExistingEmptyScene: Boolean(ctx.preferExistingEmptyScene),
            createdAtMs: Date.now(),
        };
    }

    setPendingAddSceneFromSelectionContext(ctx: { startSec: number; endSec: number } | null) {
        if (!ctx) {
            this.pendingAddSceneFromSelectionContext = null;
            return;
        }
        // Only one "pending sidebar action" should be active at a time.
        this.pendingAddLayerContext = null;
        this.pendingAddLayerFromSelectionContext = null;
        this.pendingPlaceholderReplaceContext = null;
        this.pendingSceneLayoutPickerContext = null;
        this.pendingInsertIntoScriptContext = null;

        const startSec = Number(ctx.startSec);
        const endSec = Number(ctx.endSec);
        if (!Number.isFinite(startSec) || !Number.isFinite(endSec)) {
            this.pendingAddSceneFromSelectionContext = null;
            return;
        }
        if (endSec <= startSec) {
            this.pendingAddSceneFromSelectionContext = null;
            return;
        }
        this.pendingAddSceneFromSelectionContext = {
            startSec,
            endSec,
            createdAtMs: Date.now(),
        };
    }

    consumePendingAddSceneFromSelectionContext(
        maxAgeMs: number = 60_000,
    ): { startSec: number; endSec: number } | null {
        const ctx = this.pendingAddSceneFromSelectionContext;
        this.pendingAddSceneFromSelectionContext = null;
        if (!ctx) return null;
        if (Date.now() - (ctx.createdAtMs ?? 0) > maxAgeMs) return null;
        return { startSec: ctx.startSec, endSec: ctx.endSec };
    }

    setScriptChunkClipboard(chunk: ScriptChunkV1 | null) {
        if (!chunk) {
            this.scriptChunkClipboard = null;
            return;
        }
        this.scriptChunkClipboard = { chunk, createdAtMs: Date.now() };
    }

    getScriptChunkClipboard(): ScriptChunkV1 | null {
        return this.scriptChunkClipboard?.chunk ?? null;
    }

    consumePendingInsertIntoScriptContext(
        maxAgeMs: number = 60_000,
    ): { atClipLocalSec: number; preferExistingEmptyScene?: boolean } | null {
        const ctx = this.pendingInsertIntoScriptContext;
        this.pendingInsertIntoScriptContext = null;
        if (!ctx) return null;
        if (Date.now() - (ctx.createdAtMs ?? 0) > maxAgeMs) return null;
        return { atClipLocalSec: ctx.atClipLocalSec, preferExistingEmptyScene: ctx.preferExistingEmptyScene };
    }

    peekPendingPlaceholderReplaceContext(maxAgeMs: number = 60_000): { placeholderClipId: string } | null {
        const ctx = this.pendingPlaceholderReplaceContext;
        if (!ctx) return null;
        if (Date.now() - (ctx.createdAtMs ?? 0) > maxAgeMs) {
            this.pendingPlaceholderReplaceContext = null;
            return null;
        }
        return { placeholderClipId: ctx.placeholderClipId };
    }

    consumePendingPlaceholderReplaceContext(maxAgeMs: number = 60_000): { placeholderClipId: string } | null {
        const ctx = this.pendingPlaceholderReplaceContext;
        this.pendingPlaceholderReplaceContext = null;
        if (!ctx) return null;
        if (Date.now() - (ctx.createdAtMs ?? 0) > maxAgeMs) return null;
        return { placeholderClipId: ctx.placeholderClipId };
    }

    setPendingSceneLayoutPickerContext(ctx: { sceneIndex: number } | null) {
        if (!ctx) {
            this.pendingSceneLayoutPickerContext = null;
            return;
        }
        // Only one "pending sidebar action" should be active at a time.
        this.pendingAddLayerContext = null;
        this.pendingAddLayerFromSelectionContext = null;
        this.pendingPlaceholderReplaceContext = null;
        this.pendingInsertIntoScriptContext = null;
        const sceneIndex = Number(ctx.sceneIndex);
        if (!Number.isFinite(sceneIndex) || sceneIndex < 0) {
            this.pendingSceneLayoutPickerContext = null;
            return;
        }
        this.pendingSceneLayoutPickerContext = {
            sceneIndex,
            createdAtMs: Date.now(),
        };
    }

    peekPendingSceneLayoutPickerContext(maxAgeMs: number = 60_000): { sceneIndex: number } | null {
        const ctx = this.pendingSceneLayoutPickerContext;
        if (!ctx) return null;
        if (Date.now() - (ctx.createdAtMs ?? 0) > maxAgeMs) {
            this.pendingSceneLayoutPickerContext = null;
            return null;
        }
        return { sceneIndex: ctx.sceneIndex };
    }

    consumePendingSceneLayoutPickerContext(maxAgeMs: number = 60_000): { sceneIndex: number } | null {
        const ctx = this.pendingSceneLayoutPickerContext;
        this.pendingSceneLayoutPickerContext = null;
        if (!ctx) return null;
        if (Date.now() - (ctx.createdAtMs ?? 0) > maxAgeMs) return null;
        return { sceneIndex: ctx.sceneIndex };
    }

    /**
     * Set the selected visual element on the canvas.
     * This also sets selectedClipId to the parent clip.
     * For ScriptLayerClips, also sets selectedSceneIndex based on current playhead.
     */
    setSelectedVisualElement(ref: VisualElementRef | null) {
        if (!ref) {
            this.clearSelectedVisualElement();
            return;
        }

        // Ensure clip selection + selection clearing semantics match setSelectedClipId.
        // This prevents stale scene/range selection from lingering when selecting a non-script visual on the canvas.
        this.setSelectedClipId(ref.clipId);
        this.selectedVisualElement = ref;
        this.setSelectedTransitionId(null);

        // For ScriptLayerClips, determine and set scene index based on playhead
        try {
            const clip: any = CompositionService.getClipById(ref.clipId);
            if (clip && String(clip.type) === 'script') {
                const EPS = 1e-3;
                const clipStartSec = Number(clip.start?.seconds ?? 0);
                const rate = Number(clip.playbackRate ?? 1) || 1;

                // Timeline duration is in "timeline seconds" (WaveSurfer domain).
                const dur = Number(
                    CompositionStateStore.clips?.[ref.clipId]?.trimmedDuration ??
                        clip.timelineDuration?.seconds ??
                        0
                );
                if (!Number.isFinite(dur) || dur <= EPS) return;

                const inSec = Number(clip.range?.[0]?.seconds ?? 0);
                const outSec = Number(clip.range?.[1]?.seconds ?? clip.duration?.seconds ?? inSec);

                // Scene boundary splits are stored in clip-local (source) time.
                const sceneBoundarySplits: number[] = (clip.splits ?? [])
                    .filter((s: any) => s?.isSceneBoundary === true)
                    .map((s: any) => Number(s.timeSec))
                    .filter((t: any) => Number.isFinite(t) && t > inSec + EPS && t < outSec - EPS)
                    .sort((a: number, b: number) => a - b);

                const segments: any[] = Array.isArray(clip.scriptSegments) ? clip.scriptSegments : [];
                const mapSourceToTimeline = (srcTime: number): number | null => {
                    if (!Number.isFinite(srcTime)) return null;
                    if (Math.abs(srcTime - outSec) <= EPS) return dur;

                    for (const seg of segments) {
                        const s0 = Number(seg?.srcStartSec ?? NaN);
                        const s1 = Number(seg?.srcEndSec ?? NaN);
                        const c0 = Number(seg?.compStartSec ?? NaN);
                        if (!Number.isFinite(s0) || !Number.isFinite(s1) || !Number.isFinite(c0)) continue;
                        if (srcTime >= s0 && srcTime < s1) {
                            const absCompTime = c0 + (srcTime - s0);
                            return (absCompTime - clipStartSec) / rate;
                        }
                    }

                    return null;
                };

                // Gap insertion sections are timeline-time holes (they don't correspond to source time).
                const gapInsertionSections: Array<{ start: number; end: number }> = [];
                const insertions: any[] = Array.isArray(clip.gapInsertions) ? clip.gapInsertions : [];
                for (const ins of insertions) {
                    const at = Number(ins?.atSec ?? 0);
                    const durSrc = Number(ins?.durationSec ?? 0);
                    if (!Number.isFinite(at) || !Number.isFinite(durSrc) || durSrc <= EPS) continue;

                    const durTimeline = durSrc / rate;

                    // Trailing insertion at clip end.
                    if (Math.abs(at - outSec) <= EPS) {
                        const end = dur;
                        const start = Math.max(0, end - durTimeline);
                        if (end - start > EPS) gapInsertionSections.push({ start, end });
                        continue;
                    }

                    // Find the first segment that starts at the insertion point (content after the hole).
                    const seg = segments.find((s: any) => Math.abs(Number(s?.srcStartSec ?? NaN) - at) <= EPS);
                    if (!seg || !Number.isFinite(seg.compStartSec)) continue;

                    const end = Math.max(0, Math.min(dur, (Number(seg.compStartSec) - clipStartSec) / rate));
                    const start = Math.max(0, end - durTimeline);
                    if (end - start > EPS) gapInsertionSections.push({ start, end });
                }
                gapInsertionSections.sort((a, b) => a.start - b.start);

                // Build timeline sections (scene boundaries + inserted gap boundaries).
                const points: number[] = [0, dur];
                for (const t of sceneBoundarySplits) {
                    const mapped = mapSourceToTimeline(t);
                    if (mapped == null || !Number.isFinite(mapped)) continue;
                    if (mapped <= EPS || mapped >= dur - EPS) continue;
                    points.push(mapped);
                }
                for (const g of gapInsertionSections) {
                    points.push(g.start, g.end);
                }

                points.sort((a, b) => a - b);
                const unique: number[] = [];
                for (const p of points) {
                    if (!Number.isFinite(p)) continue;
                    const clamped = Math.max(0, Math.min(dur, p));
                    if (!unique.length || Math.abs(clamped - unique[unique.length - 1]) > EPS) unique.push(clamped);
                }

                const sections: Array<{ start: number; end: number; isInsertedGap: boolean }> = [];
                for (let i = 0; i < unique.length - 1; i++) {
                    const start = unique[i];
                    const end = unique[i + 1];
                    if (end - start <= EPS) continue;
                    const isInsertedGap = gapInsertionSections.some((g) => Math.abs(g.start - start) <= EPS && Math.abs(g.end - end) <= EPS);
                    sections.push({ start, end, isInsertedGap });
                }

                // Find which section contains the playhead (timeline domain).
                const timelineTime = Number(CompositionStateStore.currentTime ?? 0) - clipStartSec;
                const t = Math.max(0, Math.min(dur - EPS, Number.isFinite(timelineTime) ? timelineTime : 0));
                const section =
                    sections.find((s) => t >= s.start && t < s.end) ??
                    (sections.length ? sections[sections.length - 1] : null);
                if (!section) return;

                // Inserted gap sections are selectable but do not map to a source-scene index.
                if (section.isInsertedGap) {
                    this.selectedSceneIndex = null;
                    this.selectedScriptLayerRange = { start: section.start, end: section.end };
                    return;
                }

                // Determine the content-scene index from scene boundaries (ignoring gap insertions).
                const scenePoints: number[] = [0, dur];
                for (const st of sceneBoundarySplits) {
                    const mapped = mapSourceToTimeline(st);
                    if (mapped == null || !Number.isFinite(mapped)) continue;
                    if (mapped <= EPS || mapped >= dur - EPS) continue;
                    scenePoints.push(mapped);
                }
                scenePoints.sort((a, b) => a - b);
                const uniqueScenePoints: number[] = [];
                for (const p of scenePoints) {
                    if (!Number.isFinite(p)) continue;
                    const clamped = Math.max(0, Math.min(dur, p));
                    if (!uniqueScenePoints.length || Math.abs(clamped - uniqueScenePoints[uniqueScenePoints.length - 1]) > EPS) {
                        uniqueScenePoints.push(clamped);
                    }
                }
                const sceneBoundaries: Array<{ start: number; end: number }> = [];
                for (let i = 0; i < uniqueScenePoints.length - 1; i++) {
                    const start = uniqueScenePoints[i];
                    const end = uniqueScenePoints[i + 1];
                    if (end - start <= EPS) continue;
                    sceneBoundaries.push({ start, end });
                }

                let contentSceneIndex = 0;
                if (sceneBoundaries.length > 0) {
                    const found = sceneBoundaries.findIndex((b) => t >= b.start && t < b.end);
                    contentSceneIndex = found >= 0 ? found : (sceneBoundaries.length - 1);
                }

                // Track the *content* scene index for scene-scoped operations, but keep the selected
                // timeline range aligned to the clicked/active SECTION (which may be subdivided by
                // inserted gaps).
                this.selectedSceneIndex = contentSceneIndex;
                this.selectedScriptLayerRange = { start: section.start, end: section.end };
            } else {
                // For non-script visuals, ensure script-scoped selection is cleared.
                this.selectedSceneIndex = null;
                this.selectedScriptLayerRange = null;
            }
        } catch {
            // Ignore errors during scene lookup (e.g., if service not initialized)
        }
    }

    clearSelectedVisualElement() {
        this.selectedVisualElement = null;
        this.selectedClipId = null;
        this.editingTextClipId = null;
        this.exitCropMode();
    }

    setEditingTextClipId(id: string | null) {
        this.editingTextClipId = id;
    }

    startEditingTextClip(clipId: string) {
        if (!clipId) return;
        this.setSelectedClipId(clipId);
        this.editingTextClipId = clipId;
    }

    setSelectedTransitionId(id: string | null) {
        this.selectedTransitionId = id;
    }

    setDraggingObject(draggingObject: DraggingObjectPayload | null) {
        this.draggingObject = draggingObject;
    }

    setRangeToolEnabled(value: boolean) {
        this.rangeToolEnabled = value;
        if (value) this.slipToolEnabled = false;
    }

    setSelectedRange(value: {start: number, end: number}) {
        this.selectedRange = value;
    }

    setSlipToolEnabled(value: boolean) {
        this.slipToolEnabled = value;
        if (value) this.rangeToolEnabled = false;
    }

    /**
     * Set selected range on script layer (for range tool selection).
     * Does NOT set scene index - use setSelectedSceneIndex for scene clicks.
     */
    setSelectedScriptLayerRange(value: {start: number, end: number} | null) {
        this.selectedScriptLayerRange = value;
        // Note: Do NOT set selectedSceneIndex here - range selection is independent
    }

    /**
     * Set selected scene by index (for scene clicks).
     * Also sets selectedScriptLayerRange to the scene's range for transcript sync.
     */
    setSelectedSceneIndex(index: number | null, sceneRange?: {start: number, end: number}) {
        this.selectedSceneIndex = index;
        // When selecting a scene, also set the range for transcript sync
        if (index !== null && sceneRange) {
            this.selectedScriptLayerRange = sceneRange;
        } else if (index === null) {
            // Clearing scene selection doesn't clear range (range tool might be active)
        }
    }

    setTheme(value: ThemeVariants = "dark") {
        if (value === "system") {
            const isDarkTheme = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
            ApplicationStore.theme = isDarkTheme ? "dark" : "light";
        } else {
            ApplicationStore.theme = value;
        }
    }

    setSceneEditScope(scope: SceneEditScope) {
        this.sceneEditScope = scope;
    }

    setFiltersPath(path: string | null) {
        this.filtersPath = path;
    }

    setEffectsPath(path: string | null) {
        this.effectsPath = path;
    }

    setTransitionsPath(path: string | null) {
        this.transitionsPath = path;
    }

    setSubtitleStylesPath(path: string | null) {
        this.subtitleStylesPath = path;
    }

    setAnimationsPath(path: string | null) {
        this.animationsPath = path;
    }

    setTitlesPath(path: string | null) {
        this.titlesPath = path;
    }

    setTransitions(transitions: Record<string, TransitionDescription>) {
        this.transitions = transitions;
    }

    setFilters(filters: Record<string, FilterDescription>) {
        this.filters = filters;
    }

    setEffects(effects: Record<string, EffectDescription>) {
        this.effects = effects;
    }

    // Crop mode actions
    enterCropMode(initialCrop?: CropConfig) {
        if (!this.selectedVisualElement) return;
        this.isCropMode = true;
        // Use provided initial crop (from clip's current crop) or default to full frame
        this.workingCrop = initialCrop ?? { x: 0, y: 0, width: 1, height: 1 };
        this.cropZoom = 1;
    }

    exitCropMode() {
        this.isCropMode = false;
    }

    setCropScope(scope: CropScope) {
        this.cropScope = scope;
    }

    setWorkingCrop(crop: CropConfig) {
        this.workingCrop = { ...crop };
    }

    setCropZoom(zoom: number) {
        this.cropZoom = Math.max(0.1, Math.min(5, zoom));
    }

    resetCrop() {
        this.workingCrop = { x: 0, y: 0, width: 1, height: 1 };
        this.cropZoom = 1;
    }

    reset() {
        this.isLoading = true;
        this.isRendering = false;
        this.selectedClipId = null;
        this.selectedVisualElement = null;
        this.selectedTransitionId = null;
        this.subtitlesClipId = null;
        this.transitions = {};
        this.filters = {};
        this.effects = {};
        this.draggingObject = null;
        this.rangeToolEnabled = false;
        this.slipToolEnabled = false;
        this.selectedScriptLayerRange = null;
        this.selectedSceneIndex = null;
        this.theme = "system";
        this.filtersPath = null;
        this.effectsPath = null;
        this.transitionsPath = null;
        this.titlesPath = null;
        this.subtitleStylesPath = null;
        this.animationsPath = null;
        // Reset crop state
        this.isCropMode = false;
        this.cropScope = 'current';
        this.workingCrop = { x: 0, y: 0, width: 1, height: 1 };
        this.cropZoom = 1;
    }
}

const ApplicationStore = new ApplicationStoreInstance();

export { ApplicationStore };
